import { Settings2 } from "lucide-react";
import { useCanAccess, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { SettingsSectionDialog } from "./SettingsSectionDialog";
import {
  SETTINGS_SECTIONS,
  type SettingsSectionId,
} from "./settingsSectionRegistry";

/**
 * A settings icon to put next to something Settings configures. It opens that
 * section of Settings in a dialog, in place.
 *
 * Desktop only for now: the phone has its own settings page and the dialogs
 * were not designed for it (Sam, 2026-10-01). Admins only, like Settings
 * itself. LCCRM-86.
 */
export const SettingsShortcut = ({
  section,
  className,
}: {
  section: SettingsSectionId;
  className?: string;
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // The same check that shows Settings in the user menu. Only admins can save
  // the configuration, so nobody else gets an icon that would fail on Save.
  const { canAccess } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });

  if (isMobile || !canAccess) return null;

  const { label, fallback } = SETTINGS_SECTIONS[section];
  const title = translate("crm.settings.edit_section", {
    name: translate(label, { smart_count: 2, _: fallback }),
  });

  return (
    <>
      <Button
        type="button"
        // Bordered, so it reads as a button and not as part of the label
        // beside it (Sam, 2026-10-01: more visible to Bill).
        variant="outline"
        size="icon"
        className={cn("size-6 cursor-pointer", className)}
        aria-label={title}
        title={title}
        onClick={() => setOpen(true)}
      >
        {/* The two-slider icon, not the gear: Sam's choice, 2026-10-01. */}
        <Settings2 className="size-3.5" />
      </Button>
      <SettingsSectionDialog
        section={section}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
};
